import React from 'react';
import { Theme } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { SxProps } from '@mui/system/styleFunctionSx';
import { BlockContainer } from '@/components/Blocks/BlockContainer';
import { BlockTitle } from '@/components/Blocks/components/BlockTitle';
import { ResponsiveSlider } from '@/components/Blocks/components/ResponsiveSlider';
import { DealCard } from '@/components/Cards/DealCard';
import { CmsHtmlContent } from '@/components/CmsHtmlContent';
import { CmsImage } from '@/components/CmsImage';
import { useGetMarketingDeals } from '@/hooks/useGetMarketingDeals';
import { CmsDealsBlock } from '@/services/cms/blocks/CmsDealsBlock';
import { CmsDeal } from '@/services/cms/models/CmsDeal';

export interface DealsBlockProps extends CmsDealsBlock {
    sx?: SxProps<Theme>;
}

export const DealsBlock = ({ sx, deals: cachedDeals, ...props }: DealsBlockProps) => {
    const { data: deals } = useGetMarketingDeals({
        region: props.regionCode,
        tags: props.fleetTypeSlug ? new Set([props.fleetTypeSlug]) : undefined,
        type: props.dealType !== 'All' ? props.dealType : undefined,
        initialData: cachedDeals,
        staleTime: 1,
        refetchOnMount: true,
        refetchOnWindowFocus: true,
    });

    return (
        <BlockContainer sx={sx} {...props}>
            <BlockTitle {...props} />
            <DealCards {...props} deals={deals} />
        </BlockContainer>
    );
};

const DealCards = ({ deals, ...props }: DealsBlockProps) => {
    if (!deals || deals.length === 0) {
        return <NoDealsMessage {...props} />;
    }

    if (props.displayMode === 'Slider') {
        return <DealSlider {...props} deals={deals} />;
    }

    return <DealGrid deals={deals} />;
};

const NoDealsMessage = (props: DealsBlockProps) => {
    if (!props.noDealsImage) {
        return <CmsHtmlContent html={props.noDealsMessage} />;
    }
    return (
        <Grid
            container
            spacing={{
                xs: 2,
                sm: 4,
            }}
        >
            <Grid
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                size={{
                    xs: 12,
                    sm: 3,
                }}
            >
                {props.noDealsImage ? <CmsImage sx={{ maxWidth: '100%', height: 'auto' }} {...props.noDealsImage} /> : null}
            </Grid>
            <Grid
                sx={{ display: 'flex', alignItems: 'center' }}
                size={{
                    xs: 12,
                    sm: 9,
                }}
            >
                <CmsHtmlContent html={props.noDealsMessage} />
            </Grid>
        </Grid>
    );
};

const DealSlider = ({ deals }: { deals: CmsDeal[] }) => {
    const count = deals.length;
    const placeHolders = Array.from({ length: count < 3 ? 3 - count : 0 }, (_, i) => i);
    return (
        <ResponsiveSlider>
            {deals.map((deal) => (
                <DealCard key={deal.code} deal={deal} />
            ))}
            {placeHolders.map((i) => (
                <div key={i} />
            ))}
        </ResponsiveSlider>
    );
};

const DealGrid = ({ deals }: { deals: CmsDeal[] }) => (
    <Grid container spacing={2}>
        {deals.map((deal) => (
            <Grid key={deal.code} size={{ sm: 6, md: 4 }}>
                <DealCard deal={deal} />
            </Grid>
        ))}
    </Grid>
);
